import Papa from "papaparse";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { t } from "i18next";

import { strings_general_App } from "../../../config/constants";

export const exportTicketsCsv = (rows, fileName) => {
    const csv = Papa.unparse(rows,{delimiter:";"});

    const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, `${fileName}.csv`);
};

// PDF theme
const PDF_COLORS = {
    brand: [11, 45, 94],
    brandAccent: [0, 122, 204],
    text: [33, 37, 41],
    muted: [108, 117, 125],
    border: [222, 226, 230],
    zebra: [246, 248, 251],
    metaBg: [243, 246, 250],
    success: [25, 135, 84],
    danger: [220, 53, 69],
    warning: [176, 120, 0],
};

const PDF_MARGIN = 12;

const getStatusColor = (value) => {
    const v = String(value ?? "").toLowerCase();
    if (v === "active" || v === String(t("boarded")).toLowerCase()) return PDF_COLORS.success;
    if (v === "no show" || v === "inactive" || v === "disabled") return PDF_COLORS.danger;
    if (v === "pending") return PDF_COLORS.warning;
    return null;
};

const isNumericColumn = (rows, header) =>
    rows.every(r => r[header] === "" || r[header] === "-" || !Number.isNaN(Number(r[header])));

export const exportTicketsPdf = ({
    rows,
    fileName,
    title,
    description,
    departureTime,
    filters = [],
}) => {
    if (!rows || rows.length === 0) return;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const generatedOn = new Date().toLocaleString("it-IT");

    // ---- Header band (first page) ----
    doc.setFillColor(...PDF_COLORS.brand);
    doc.rect(0, 0, pageWidth, 24, "F");
    doc.setFillColor(...PDF_COLORS.brandAccent);
    doc.rect(0, 24, pageWidth, 1.2, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text(title || "", PDF_MARGIN, 15.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(strings_general_App.NAME_CUSTOMER, pageWidth - PDF_MARGIN, 11, { align: "right" });
    doc.setFontSize(8);
    doc.text(`${t("generated_on")}: ${generatedOn}`, pageWidth - PDF_MARGIN, 17, { align: "right" });

    // ---- Summary box ----
    const meta = [];
    if (description) meta.push(["", description]);
    if (departureTime) meta.push([t("departure_time"), departureTime]);
    if (filters.length > 0) {
        meta.push([t("filters"), filters.map(([label, value]) => `${label}: ${value}`).join("  ·  ")]);
    }
    meta.push([t("total"), String(rows.length)]);

    const metaTop = 31;
    const lineHeight = 5.5;
    const metaHeight = meta.length * lineHeight + 5;

    doc.setFillColor(...PDF_COLORS.metaBg);
    doc.setDrawColor(...PDF_COLORS.border);
    doc.roundedRect(PDF_MARGIN, metaTop, pageWidth - PDF_MARGIN * 2, metaHeight, 1.5, 1.5, "FD");

    meta.forEach(([label, value], i) => {
        const y = metaTop + 6 + i * lineHeight;
        let x = PDF_MARGIN + 4;
        doc.setFontSize(9);
        if (label) {
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...PDF_COLORS.muted);
            doc.text(`${label}:`, x, y);
            x += doc.getTextWidth(`${label}: `) + 1;
        }
        doc.setFont("helvetica", label ? "normal" : "bold");
        doc.setTextColor(...PDF_COLORS.text);
        // Keep long values (e.g. many filters) on one line inside the box
        const maxWidth = pageWidth - PDF_MARGIN - 4 - x;
        const lines = doc.splitTextToSize(String(value), maxWidth);
        doc.text(lines.length > 1 ? `${lines[0]}...` : String(value), x, y);
    });

    // ---- Table ----
    const headers = Object.keys(rows[0]);
    const body = rows.map(r => headers.map(h => r[h] ?? "-"));
    const statusIndex = headers.indexOf(t("status"));

    const columnStyles = {};
    headers.forEach((h, i) => {
        if (isNumericColumn(rows, h)) columnStyles[i] = { halign: "center" };
    });

    autoTable(doc, {
        startY: metaTop + metaHeight + 6,
        head: [headers],
        body,
        theme: "grid",
        margin: { top: 20, left: PDF_MARGIN, right: PDF_MARGIN, bottom: 16 },
        styles: {
            font: "helvetica",
            fontSize: headers.length > 12 ? 6.8 : 8,
            cellPadding: { top: 2.2, bottom: 2.2, left: 2, right: 2 },
            textColor: PDF_COLORS.text,
            lineColor: PDF_COLORS.border,
            lineWidth: 0.15,
            valign: "middle",
            overflow: "linebreak",
        },
        headStyles: {
            fillColor: PDF_COLORS.brand,
            textColor: 255,
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
        },
        alternateRowStyles: {
            fillColor: PDF_COLORS.zebra,
        },
        columnStyles,
        didParseCell: (data) => {
            if (data.section === "body" && data.column.index === statusIndex) {
                const color = getStatusColor(data.cell.raw);
                if (color) {
                    data.cell.styles.textColor = color;
                    data.cell.styles.fontStyle = "bold";
                }
            }
        },
    });

    // ---- Running header (pages 2+) and footer (all pages) ----
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        if (i > 1) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(...PDF_COLORS.brand);
            doc.text(title || "", PDF_MARGIN, 12);
            doc.setDrawColor(...PDF_COLORS.brandAccent);
            doc.setLineWidth(0.5);
            doc.line(PDF_MARGIN, 14.5, pageWidth - PDF_MARGIN, 14.5);
        }

        doc.setDrawColor(...PDF_COLORS.border);
        doc.setLineWidth(0.3);
        doc.line(PDF_MARGIN, pageHeight - 11, pageWidth - PDF_MARGIN, pageHeight - 11);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...PDF_COLORS.muted);
        doc.text(
            `${strings_general_App.NAME_CUSTOMER} · ${strings_general_App.POWERED_BLUENET}`,
            PDF_MARGIN,
            pageHeight - 6
        );
        doc.text(
            `${t("page")} ${i} ${t("of")} ${totalPages}`,
            pageWidth - PDF_MARGIN,
            pageHeight - 6,
            { align: "right" }
        );
    }

    doc.save(`${fileName}.pdf`);
};